import { useEffect } from 'react'
import { message } from 'antd'
import { auth, database } from './firebase'
import { BUILD_ID, RELOAD_CONFIG_PATH } from './version'

/**
 * Auto-reload stale tabs after a new version is deployed.
 *
 * Mechanism: a single lightweight listener on RTDB `config/delegationApp`. When ops bumps
 * `minBuild` (or `reloadSignal`) above the build id this tab is running, we show a brief
 * antd toast and then `window.location.reload()` — so non-technical agents never have to
 * hard-refresh. The hosting `Cache-Control: no-store` header guarantees the reload fetches
 * the new bundle.
 *
 * IMPORTANT: `config` requires `auth.uid != null` to read, and this hook is mounted at the App
 * root which renders BEFORE login. So we attach the listener only once Firebase reports an
 * authenticated user (onAuthStateChanged), and re-attach across sign-in/out. Attaching while
 * unauthenticated silently yields no events (permission denied) — that was the original bug.
 *
 * Cost note: this is the ONLY always-on listener added, on a tiny node that changes only on
 * deploy — its egress is a few bytes, unrelated to the per-chat egress this app was fixed for.
 *
 * Safety:
 * - Never reloads when BUILD_ID is unknown (0) — avoids dev/loop surprises.
 * - Only reloads when the server build is strictly greater than the running build.
 * - sessionStorage guard so a single signal can never loop-reload the same tab.
 * - Detaches the DB listener and the auth subscription on unmount.
 */
const RELOAD_GUARD_KEY = 'ydo_reloaded_for_build'
const RELOAD_DELAY_MS = 5000

export function useForceReload() {
  useEffect(() => {
    // In dev or an un-stamped build we don't know our own version — do nothing rather than risk a loop.
    if (!BUILD_ID) return undefined

    const ref = database.ref(RELOAD_CONFIG_PATH)
    let reloadTimer = null
    let attached = false

    const handler = (snapshot) => {
      const cfg = snapshot.val() || {}
      // Accept either an explicit minBuild or a reloadSignal timestamp; take the larger.
      const serverBuild = Number(cfg.minBuild || cfg.reloadSignal || 0)
      if (!serverBuild || serverBuild <= BUILD_ID) return

      // Already handled this exact target build in this tab? Don't loop.
      if (Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) === serverBuild) return
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(serverBuild))

      if (reloadTimer) return
      message.info('A new version is available — updating…', RELOAD_DELAY_MS / 1000)
      reloadTimer = setTimeout(() => {
        window.location.reload()
      }, RELOAD_DELAY_MS)
    }

    const onError = (err) => {
      // Surface denials instead of failing silently; the listener stays detached until next sign-in.
      // eslint-disable-next-line no-console
      console.warn('[useForceReload] config listener error:', err && err.code)
      attached = false
    }

    const attach = () => {
      if (attached) return
      attached = true
      ref.on('value', handler, onError)
    }
    const detach = () => {
      if (!attached) return
      attached = false
      ref.off('value', handler)
    }

    // Attach only when authenticated (config read requires auth); re-evaluate on sign-in/out.
    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (user) attach()
      else detach()
    })

    return () => {
      unsubAuth()
      detach()
      if (reloadTimer) clearTimeout(reloadTimer)
    }
  }, [])
}

export default useForceReload
